import React from 'react'
import clsx from 'clsx'

export interface GraphParameter {
  value: string
  label: string
  color: string
}

export interface GraphParameterSelectorProps {
  parameters: GraphParameter[]
  selected: string[]
  onChange: (selected: string[]) => void
  /** Parameters whose telemetry is all-zero/null in the loaded samples should
   *  be set to `false` here. Defaults to "everything available". */
  availableData?: Record<string, boolean>
  title?: string
  className?: string
  /** Rendered before Select All in the header (e.g. Tilt gap break). */
  headerActions?: React.ReactNode
}

/**
 * Horizontal grid of checkbox cards used by the Pass Analytics Graph tab. One
 * card per supported parameter (see `passAnalyticsGraphConfig`). Cards for
 * parameters with no usable data are visually muted and don't fire onChange.
 *
 * The "Select All / Deselect All" toggle only counts (and acts on) parameters
 * that have data — empty parameters stay deselected regardless.
 */
export function GraphParameterSelector({
  parameters,
  selected,
  onChange,
  availableData = {},
  title = 'Select Parameters to Display',
  className,
  headerActions
}: GraphParameterSelectorProps) {
  const isAvailable = (value: string) => availableData[value] !== false
  const availableParameters = parameters.filter((p) => isAvailable(p.value))
  const allAvailableSelected =
    availableParameters.length > 0 &&
    availableParameters.every((p) => selected.includes(p.value))

  const toggleOne = (value: string) => {
    if (!isAvailable(value)) return
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value))
    } else {
      onChange([...selected, value])
    }
  }

  const toggleAll = () => {
    if (availableParameters.length === 0) return
    if (allAvailableSelected) {
      onChange([])
    } else {
      onChange(availableParameters.map((p) => p.value))
    }
  }

  const unavailableCount = parameters.length - availableParameters.length

  return (
    <div
      className={clsx(
        'rounded-lg border border-[#E2E5EA] bg-white p-4',
        className
      )}
      data-testid="graph-parameter-selector"
    >
      <div className="mb-4 flex items-center justify-between gap-3">
        <h3 className="min-w-0 flex-1 text-sm font-semibold text-heritage-grey">{title}</h3>
        <div className="flex flex-shrink-0 items-center gap-2">
          {headerActions}
          <button
            type="button"
            data-testid="graph-param-select-all"
            onClick={toggleAll}
            disabled={availableParameters.length === 0}
            className={clsx(
              'rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors',
              availableParameters.length === 0
                ? 'cursor-not-allowed border-[#EEF0F3] text-[#C9CDD4]'
                : 'border-primary-200 text-primary-700 hover:bg-primary-50'
            )}
          >
            {availableParameters.length === 0
              ? 'No Data Available'
              : allAvailableSelected
                ? 'Deselect All'
                : `Select All (${availableParameters.length})`}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 md:grid-cols-3 lg:grid-cols-4">
        {parameters.map((p) => {
          const available = isAvailable(p.value)
          const checked = selected.includes(p.value)
          return (
            <label
              key={p.value}
              data-testid={`graph-param-${p.value}`}
              data-value={p.value}
              className={clsx(
                'flex select-none items-center gap-2 rounded-md border p-2 transition-colors',
                !available && 'cursor-not-allowed border-[#EEF0F3] bg-page-bg/40 opacity-50',
                available && checked && 'cursor-pointer border-primary-300 bg-primary-50/40',
                available && !checked && 'cursor-pointer border-[#E2E5EA] hover:border-[#C9CDD4]'
              )}
            >
              <input
                type="checkbox"
                checked={checked}
                disabled={!available}
                onChange={() => toggleOne(p.value)}
                className="h-4 w-4 cursor-pointer rounded border-heritage-grey text-hero-red focus:outline-none focus:ring-2 focus:ring-hero-red/40 disabled:cursor-not-allowed"
              />
              <span
                data-testid="graph-param-dot"
                aria-hidden
                className="h-3 w-3 flex-shrink-0 rounded-full"
                style={{ backgroundColor: p.color }}
              />
              <span
                className={clsx(
                  'truncate text-xs',
                  checked ? 'font-semibold text-primary-700' : 'text-text-grey'
                )}
              >
                {p.label}
              </span>
            </label>
          )
        })}
      </div>

      <p className="mt-3 text-center text-xs text-heritage-grey">
        <span className="font-semibold text-primary-700">{selected.length}</span> of{' '}
        {availableParameters.length} available parameter
        {availableParameters.length === 1 ? '' : 's'} selected
        {unavailableCount > 0 ? (
          <span className="ml-1 text-[#C9CDD4]">
            ({unavailableCount} parameter{unavailableCount === 1 ? '' : 's'} have no data)
          </span>
        ) : null}
      </p>
    </div>
  )
}

export default GraphParameterSelector
